import type { CellContext, ColumnDef } from '@tanstack/react-table'

import { AMOUNT_TONE, amountTone, numberToCurrency } from '@/utils'
import type { MerchantRow } from './types'

const AbsAmountCell = (cell: CellContext<MerchantRow, unknown>) => {
  const amount = cell.getValue() as number
  return (
    <span className={`block truncate font-normal ${amountTone(amount)}`}>
      {numberToCurrency(Math.abs(amount))}
    </span>
  )
}

const MoneyOutCell = (cell: CellContext<MerchantRow, unknown>) => (
  <span className={`block truncate font-normal ${AMOUNT_TONE.negative}`}>
    {numberToCurrency(cell.getValue() as number)}
  </span>
)

const moneyInOutColumns: ColumnDef<MerchantRow, unknown>[] = [
  {
    accessorKey: 'moneyIn',
    sortUndefined: 'last',
    header: 'Money in',
    size: 150,
    cell: AbsAmountCell,
  },
  {
    accessorKey: 'moneyOut',
    sortUndefined: 'last',
    header: 'Money out',
    size: 150,
    cell: MoneyOutCell,
  },
]

export function merchantsColumns(
  showMoneyInOut: boolean,
): ColumnDef<MerchantRow, unknown>[] {
  return [
    {
      accessorKey: 'merchantGroup',
      header: 'Merchant',
      size: 260,
      meta: { isFluid: true },
      cell: (cell) => {
        const { merchantGroup, names, originalNames, variationCount } =
          cell.row.original
        return (
          <div className="min-w-0">
            <span className="block truncate font-medium capitalize">
              {merchantGroup}
            </span>
            {variationCount > 1 && (
              <span className="block truncate text-muted-foreground text-xs">
                {names?.join(', ') ?? originalNames}
              </span>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'variationCount',
      header: 'Variations',
      size: 110,
    },
    {
      accessorKey: 'count',
      header: 'Transactions',
      size: 130,
    },
    {
      accessorKey: 'amount',
      header: 'Net amount',
      size: 150,
      cell: AbsAmountCell,
    },
    {
      accessorKey: 'avgAmount',
      header: 'Avg / txn',
      size: 140,
      cell: AbsAmountCell,
    },
    ...(showMoneyInOut ? moneyInOutColumns : []),
  ]
}
